/**
 * SMART on FHIR (EHR launch and standalone launch) using the `fhirclient` library.
 *
 * The browser reads the chart with the clinician's own token and posts a Bundle to the
 * backend for assessment; write-back is posted to the EHR from the browser with the
 * same token, so the backend never holds EHR credentials in this mode.
 */
import FHIR from "fhirclient";
import type Client from "fhirclient/lib/Client";
import type { SmartSession } from "./types";

export const SMART_CLIENT_ID = import.meta.env.VITE_SMART_CLIENT_ID ?? "pulse-pedobesity-dev";

export const SMART_SCOPES = [
  "launch",
  "launch/patient",
  "openid",
  "fhirUser",
  "patient/Patient.read",
  "patient/Observation.read",
  "patient/Condition.read",
  "patient/MedicationRequest.read",
  "patient/FamilyMemberHistory.read",
  "patient/Observation.write",
  "patient/ServiceRequest.write",
  "patient/CarePlan.write",
  "patient/DocumentReference.write",
  "patient/Provenance.write",
].join(" ");

export function authorize(): Promise<void> {
  return FHIR.oauth2.authorize({
    clientId: SMART_CLIENT_ID,
    scope: SMART_SCOPES,
    redirectUri: "./index.html",
  }) as Promise<void>;
}

let client: Client | null = null;

export async function ready(): Promise<Client> {
  if (!client) client = await FHIR.oauth2.ready();
  return client;
}

/** Read everything the engine needs for the in-context patient as one collection Bundle. */
export async function readChart(c: Client): Promise<{ bundle: unknown; session: SmartSession }> {
  const pid = c.patient.id;
  if (!pid) throw new Error("No patient in launch context");
  const opts = { pageLimit: 10, flat: true } as const;
  const [patient, obs, conds, meds, fmh] = await Promise.all([
    c.patient.read(),
    c.request(`Observation?patient=${pid}&_count=200`, opts).catch(() => []),
    c.request(`Condition?patient=${pid}&_count=200`, opts).catch(() => []),
    c.request(`MedicationRequest?patient=${pid}&status=active`, opts).catch(() => []),
    c.request(`FamilyMemberHistory?patient=${pid}`, opts).catch(() => []),
  ]);
  const resources = [patient, ...(obs as unknown[]), ...(conds as unknown[]), ...(meds as unknown[]), ...(fmh as unknown[])];
  let clinicianName: string | null = null;
  let practitionerRef: string | null = null;
  try {
    const user = await c.user.read();
    practitionerRef = c.user.fhirUser ?? null;
    const n = (user as { name?: { text?: string; given?: string[]; family?: string }[] }).name?.[0];
    clinicianName = n?.text ?? ([...(n?.given ?? []), n?.family].filter(Boolean).join(" ") || null);
  } catch {
    /* fhirUser not granted */
  }
  return {
    bundle: { resourceType: "Bundle", type: "collection", entry: resources.map((r) => ({ resource: r })) },
    session: {
      fhirServer: c.state.serverUrl,
      patientFhirId: pid,
      practitionerRef,
      clinicianName,
      encounterRef: c.encounter.id ? `Encounter/${c.encounter.id}` : null,
    },
  };
}

/** Post the clinician-confirmed transaction to the EHR with the SMART token. */
export async function writeBack(bundle: unknown): Promise<unknown> {
  const c = await ready();
  return c.request({
    url: "",
    method: "POST",
    body: JSON.stringify(bundle),
    headers: { "Content-Type": "application/fhir+json" },
  });
}

/** True when this page load is a SMART redirect back from the EHR, or a SMART session exists. */
export function isSmartContext(): boolean {
  const q = new URLSearchParams(window.location.search);
  if (q.get("code") && q.get("state")) return true;
  try {
    return !!sessionStorage.getItem("SMART_KEY");
  } catch {
    return false;
  }
}
